import { AnimatePresence, motion } from "framer-motion";
import { Check, Clock, X } from "lucide-react";
import { useLanguage } from "@/i18n";
import { scrollToId } from "@/components/Navbar";

export default function TourModal({ tour, onClose, onRequestTour }) {
  const { lang, t } = useLanguage();

  return (
    <AnimatePresence>
      {tour && (
        <motion.div
          data-testid="tour-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-6"
        >
          <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" onClick={onClose} />
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-[#FDFBF7] w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-t-2xl sm:rounded-sm grid md:grid-cols-2"
          >
            <button
              data-testid="tour-modal-close"
              onClick={onClose}
              aria-label={t.modal.close}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-[#FDFBF7]/90 backdrop-blur flex items-center justify-center text-ink hover:bg-[#C9A227] hover:text-[#0F2C50] transition-colors duration-300"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="relative flex flex-col h-64 md:h-auto md:min-h-[560px]">
              <div className="relative flex-1 min-h-[220px]">
                <img src={tour.image} alt={tour[lang].title} className="absolute inset-0 w-full h-full object-cover" />
              </div>
              {tour.image2 && (
                <div className="relative flex-1 min-h-[220px] border-t-4 border-[#FDFBF7]">
                  <img src={tour.image2} alt={`${tour[lang].title} (2)`} className="absolute inset-0 w-full h-full object-cover" />
                </div>
              )}
            </div>
            <div className="p-8 lg:p-12 flex flex-col">
              {tour.day && (
                <span className="self-start rounded-full bg-[#C9A227] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#0F2C50] mb-5">
                  {t.tours[tour.day]}
                </span>
              )}
              <h3 className="font-serif text-3xl lg:text-4xl font-medium tracking-tight text-ink">{tour[lang].title}</h3>
              <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-sage">
                <Clock className="w-3.5 h-3.5" /> {t.tours.fullDay}
              </p>
              <p className="mt-5 text-base leading-relaxed text-ink/75">{tour[lang].desc}</p>
              <p className="mt-7 text-xs font-bold uppercase tracking-[0.2em] text-azure">{t.modal.highlights}</p>
              <ul className="mt-3 space-y-2.5">
                {tour[lang].highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 text-sm text-ink/80">
                    <Check className="w-4 h-4 mt-0.5 text-[#C9A227] shrink-0" strokeWidth={2.5} />
                    {h}
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-8">
                <button
                  data-testid="tour-modal-cta"
                  onClick={() => {
                    onRequestTour(tour[lang].title);
                    onClose();
                    setTimeout(() => scrollToId("#richiedi"), 150);
                  }}
                  className="w-full rounded-full bg-azure px-8 py-4 text-xs font-bold uppercase tracking-[0.15em] text-[#FDFBF7] hover:bg-[#C9A227] hover:text-[#0F2C50] transition-colors duration-300"
                >
                  {t.modal.cta}
                </button>
                <p className="mt-3 text-center text-xs text-sage">{t.modal.note}</p>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
